// Shared "Recent Transactions" card — reusable across department dashboards. Sources from the
// same generic endpoint Top Level's dashboard already uses (GET /Reports/ViewTransactionLogGrid,
// TranType='Select All'), which is CC-scoped server-side by RoleId/UserId (see
// [[dashboard-transaction-log-cc-scoping]] memory — some transaction types aren't yet
// CC-filtered in the SP itself; Client/Vendor Invoice/Cash/General rows are).
import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    fetchTodayTransactionLog,
    selectTodayTransactionLog,
    selectTodayTransactionLogLoading,
} from '../../slices/financialReportSlice/transactionLogSlice';
import { formatCurrency } from '../../utilities/formatCurrency';

const RecentTransactionsCard = ({ roleId, userId, limit = 5 }) => {
    const dispatch = useDispatch();
    const todayTransactions = useSelector(selectTodayTransactionLog);
    const todayTransactionsLoading = useSelector(selectTodayTransactionLogLoading);

    useEffect(() => {
        dispatch(fetchTodayTransactionLog({ roleId, userId }));
    }, [dispatch, roleId, userId]);

    const recentTransactions = Array.isArray(todayTransactions?.Data) ? todayTransactions.Data.slice(0, limit) :
        Array.isArray(todayTransactions) ? todayTransactions.slice(0, limit) : [];

    return (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 hover:shadow-lg dark:hover:shadow-xl transition-all">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Today's Recent Activity</h3>
                <button className="text-sm text-[#0d1b5e] dark:text-orange-400 hover:text-[#0a1240] dark:hover:text-orange-300 transition-colors">View All</button>
            </div>

            {todayTransactionsLoading ? (
                <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0d1b5e] dark:border-orange-400"></div>
                    <span className="ml-2 text-gray-500 dark:text-gray-400">Loading today's transactions...</span>
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Entry Date
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Account Name
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Voucher Type
                                </th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Debit
                                </th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Credit
                                </th>
                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Status
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {recentTransactions.length > 0 ? recentTransactions.map((transaction, index) => (
                                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                        {transaction.EntryDate || transaction.Date || '-'}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                        <div className="max-w-xs truncate" title={transaction.NameofAccount || transaction.AccountName || transaction.Description || transaction.Name}>
                                            {transaction.NameofAccount || transaction.AccountName || transaction.Description || transaction.Name || '-'}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                                        <span className="px-2 py-1 text-xs font-medium bg-[#0d1b5e]/10 dark:bg-[#0d1b5e]/30 text-[#0d1b5e] dark:text-white rounded-full">
                                            {transaction.VoucherType || transaction.TranType || transaction.TransactionType || '-'}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-right font-medium">
                                        <span className="text-red-600 dark:text-red-400">
                                            ₹{formatCurrency(transaction.DebitValue || transaction.Debit || 0)}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-right font-medium">
                                        <span className="text-green-600 dark:text-green-400">
                                            ₹{formatCurrency(transaction.CreditValue || transaction.Credit || 0)}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${(transaction.Status || 'Approved').toLowerCase() === 'approved'
                                            ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400'
                                            : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400'
                                            }`}>
                                            {transaction.Status || 'Approved'}
                                        </span>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="6" className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                                        No transactions found for today
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default RecentTransactionsCard;
